// Perfil aduanero del tenant — alimenta el config del Anexo PDF (gestor/aduana).
//
// Calco deliberado del flujo probado de src/desarrollos/cliente/MiPerfil.jsx:
// baseline `cargado` vs `form` para el gate de Guardar, rebase del baseline al
// guardar (el botón se apaga solo, sin recargar), destello "Guardado" con
// temporizadores limpiados al desmontar. Diferencias a propósito:
//   · Los cuatro campos son editables — acá guarda el staff autenticado, no un
//     cliente anónimo, así que no_registro entra al loop (el adapter lo acepta).
//   · Sin estado 'vacio': getPerfilAduanero nunca devuelve null — la ausencia
//     llega como perfil en blanco con estado 'borrador' y cae en 'listo'.
//   · La validación avisa inline pero solo los dos códigos DGA condicionan el
//     guardado: es la misma regla estado='activado' que el adapter deriva, y un
//     perfil sin esos códigos bloquea el Anexo (de ahí el aviso ámbar).
// Piel Tailwind como el resto de las pestañas: el skin .amb-* vive solo dentro
// del drawer (.amb-ext), no en los paneles.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getPerfilAduanero, savePerfilAduanero } from '../../api/supabase/viajeroEncomienda';

const EDITABLES = [
  { key: 'codigo_gestor', label: 'Código de Gestor', placeholder: 'GEST-0000', maxLength: 12 },
  { key: 'codigo_aduana', label: 'Código de Aduana', placeholder: '10520', maxLength: 6, inputMode: 'numeric' },
  { key: 'nombre_gestor', label: 'Nombre del Gestor', placeholder: 'Nombre y apellido', maxLength: 80 },
  { key: 'no_registro', label: 'No. de Registro', placeholder: 'SV-849302', maxLength: 14 },
];

const VACIO = { codigo_gestor: '', codigo_aduana: '', nombre_gestor: '', no_registro: '' };

const aFormulario = (row) => ({
  codigo_gestor: row?.codigo_gestor || '',
  codigo_aduana: row?.codigo_aduana || '',
  nombre_gestor: row?.nombre_gestor || '',
  no_registro: row?.no_registro || '',
});

// Devuelve { level: 'warn' | 'error' | null, message }. Mismas reglas que el
// PerfilAduanero de Desarrollos; un campo vacío es neutro (el aviso ámbar de
// activación ya cubre la ausencia de los códigos).
const validarCampo = (key, raw) => {
  const value = raw.trim();
  if (!value) return { level: null, message: '' };

  switch (key) {
    case 'codigo_gestor':
      if (!/^[A-Z0-9-]+$/i.test(value))
        return { level: 'error', message: 'Solo se permiten letras, números y guiones.' };
      if (value.length < 4)
        return { level: 'warn', message: 'El código debe tener entre 4 y 12 caracteres.' };
      return { level: null, message: '' };

    case 'codigo_aduana':
      if (!/^\d+$/.test(value))
        return { level: 'error', message: 'El código aduana solo lleva números. Revisá que no tenga letras.' };
      if (value.length < 4)
        return { level: 'warn', message: 'Debe tener entre 4 y 6 dígitos.' };
      return { level: null, message: '' };

    case 'nombre_gestor':
      if (value.length < 3)
        return { level: 'warn', message: 'Escribí el nombre completo del gestor (mínimo 3 caracteres).' };
      return { level: null, message: '' };

    case 'no_registro':
      if (!/^[A-Z0-9-]+$/i.test(value))
        return { level: 'warn', message: 'Solo se permiten letras, números y guiones.' };
      if (value.length < 6)
        return { level: 'warn', message: 'El No. de Registro suele tener al menos 6 caracteres.' };
      return { level: null, message: '' };

    default:
      return { level: null, message: '' };
  }
};

const PerfilAduaneroTab = () => {
  const [estado, setEstado] = useState('cargando'); // cargando | listo | error
  const [intento, setIntento] = useState(0);

  const [cargado, setCargado] = useState(VACIO);
  const [form, setForm] = useState(VACIO);

  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');
  const [okMontado, setOkMontado] = useState(false);
  const [okDesvanecido, setOkDesvanecido] = useState(false);
  const temporizadores = useRef([]);

  const limpiarTemporizadores = () => {
    temporizadores.current.forEach(clearTimeout);
    temporizadores.current = [];
  };

  useEffect(() => () => limpiarTemporizadores(), []);

  useEffect(() => {
    let cancelado = false;
    setEstado('cargando');
    (async () => {
      try {
        const perfil = await getPerfilAduanero();
        if (cancelado) return;
        setCargado(aFormulario(perfil));
        setForm(aFormulario(perfil));
        setEstado('listo');
      } catch (err) {
        console.error('[ViajeroEncomienda/perfil-aduanero] error cargando el perfil', err);
        if (!cancelado) setEstado('error');
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [intento]);

  const sucio = useMemo(
    () => EDITABLES.some((c) => form[c.key].trim() !== cargado[c.key]),
    [form, cargado]
  );

  const validaciones = useMemo(
    () => Object.fromEntries(EDITABLES.map((c) => [c.key, validarCampo(c.key, form[c.key])])),
    [form]
  );

  // Solo los dos códigos DGA condicionan el guardado (regla estado='activado');
  // nombre y registro avisan pero nunca bloquean.
  const codigosInvalidos =
    validaciones.codigo_gestor.level === 'error' || validaciones.codigo_aduana.level === 'error';

  const sinCodigos = !form.codigo_gestor.trim() || !form.codigo_aduana.trim();

  const cambiar = (key) => (e) => {
    setErrorGuardar('');
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const guardar = async () => {
    if (!sucio || guardando || codigosInvalidos) return;
    limpiarTemporizadores();
    setOkMontado(false);
    setErrorGuardar('');
    setGuardando(true);

    try {
      const guardado = await savePerfilAduanero({
        codigo_gestor: form.codigo_gestor.trim(),
        codigo_aduana: form.codigo_aduana.trim(),
        nombre_gestor: form.nombre_gestor.trim(),
        no_registro: form.no_registro.trim(),
      });

      // La referencia se mueve al valor recién guardado: el botón se apaga solo.
      const valores = aFormulario(guardado);
      setForm(valores);
      setCargado(valores);

      setOkDesvanecido(false);
      setOkMontado(true);
      temporizadores.current.push(setTimeout(() => setOkDesvanecido(true), 2000));
      temporizadores.current.push(setTimeout(() => setOkMontado(false), 2400));
    } catch (err) {
      console.error('[ViajeroEncomienda/perfil-aduanero] error guardando el perfil', err);
      setErrorGuardar(err?.message || 'No se pudo guardar. Intentá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-[#f8fafc] px-4 py-4">
      <section className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-bold text-slate-900">Perfil aduanero</h2>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Datos del gestor ante la DGA. Se imprimen en el encabezado del Anexo de cada viaje.
        </p>

        {estado === 'cargando' && (
          <div className="mt-5 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        )}

        {estado === 'error' && (
          <div className="mt-5 flex flex-col items-start gap-2">
            <p className="text-xs text-red-600">
              No pudimos cargar el perfil. Revisá tu señal y volvé a intentar.
            </p>
            <button
              type="button"
              onClick={() => setIntento((n) => n + 1)}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-slate-200 px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Reintentar
            </button>
          </div>
        )}

        {estado === 'listo' && (
          <>
            {sinCodigos && (
              <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                Completá gestor y aduana para poder generar el Anexo.
              </p>
            )}

            <div className="mt-5 space-y-4">
              {EDITABLES.map((c) => {
                const v = validaciones[c.key];
                return (
                  <div key={c.key} className="flex flex-col gap-1">
                    <label
                      htmlFor={`vj-perfil-${c.key}`}
                      className="text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {c.label}
                    </label>
                    <input
                      id={`vj-perfil-${c.key}`}
                      type="text"
                      autoComplete="off"
                      inputMode={c.inputMode}
                      maxLength={c.maxLength}
                      value={form[c.key]}
                      onChange={cambiar(c.key)}
                      placeholder={c.placeholder}
                      className={`h-11 w-full rounded-lg border bg-[#f8fafc] px-3 text-sm text-slate-900 outline-none transition-colors focus:border-[var(--brand-teal)] ${
                        v.level === 'error' ? 'border-red-300' : 'border-slate-200'
                      }`}
                    />
                    {v.level && (
                      <p
                        className={`text-[11px] ${
                          v.level === 'error' ? 'text-red-600' : 'text-amber-700'
                        }`}
                      >
                        {v.message}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              {okMontado && (
                <span
                  className={`text-xs font-semibold text-emerald-600 transition-opacity duration-300 ${
                    okDesvanecido ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  Guardado
                </span>
              )}
              {errorGuardar && (
                <span role="alert" className="text-xs font-semibold text-red-600">
                  {errorGuardar}
                </span>
              )}
              <button
                type="button"
                onClick={guardar}
                disabled={!sucio || guardando || codigosInvalidos}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[var(--brand-teal)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-teal-hover)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                {guardando && <Loader2 size={14} className="animate-spin" />}
                Guardar cambios
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PerfilAduaneroTab;
